import type { AccountsBucket, AccountsState } from '@type/store';
import type {
  HoaHead,
  HoaOption,
  ParticularItem,
  ReconEntry,
  ReconciliationMatch,
  ReconciliationResult,
} from '@feature/accounts/types/accounts-page';

export const ACCOUNT_TYPES = ['RFA', 'AAA', 'PASSBOOK', 'DDO'] as const;

export type AccountType = typeof ACCOUNT_TYPES[number];

export const ACCOUNT_LABELS: Record<AccountType, string> = {
  RFA: 'RFA (Revolving Fund Account · ADB/AOB)',
  AAA: 'AAA (Asaan Assignment Account · GoPb)',
  PASSBOOK: 'Pass Book (Reconciliation)',
  DDO: 'BOP DDO Account',
};

export const ACCOUNT_SHORT: Record<AccountType, string> = {
  RFA: 'RFA',
  AAA: 'AAA',
  PASSBOOK: 'Pass Book',
  DDO: 'DDO',
};

export function emptyAccountBuckets(): Record<AccountType, AccountsBucket> {
  const empty = { entries: [], fundsReleased: { pkr: 0, usd: 0 }, fundsUtilization: { pkr: 0, usd: 0 } };
  return {
    RFA: { ...empty, entries: [] },
    AAA: { ...empty, entries: [] },
    PASSBOOK: { ...empty, entries: [] },
    DDO: { ...empty, entries: [] },
  };
}

export function parseAccountsDate(dateStr: string): Date | null {
  if (!dateStr) return null;
  const iso = new Date(dateStr);
  if (!Number.isNaN(iso.getTime())) return iso;
  const dot = dateStr.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (dot) return new Date(+dot[3], +dot[2] - 1, +dot[1]);
  const slash = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slash) return new Date(+slash[3], +slash[2] - 1, +slash[1]);
  return null;
}

export function matchesAccountsDateFilter(dateStr: string, year: string, month: string): boolean {
  if (!year && !month) return true;
  const d = parseAccountsDate(dateStr);
  if (!d) return !year && !month;
  if (year && String(d.getFullYear()) !== year) return false;
  if (month && String(d.getMonth() + 1) !== month) return false;
  return true;
}

export type AccountsUtilizationPeriod = 'yearly' | 'quarterly' | 'monthly';

export function matchesAccountsUtilizationPeriod(
  dateStr: string,
  period: AccountsUtilizationPeriod,
  year: string,
  month: string,
  quarter: string,
): boolean {
  if (period === 'yearly') {
    if (!year) return true;
    const d = parseAccountsDate(dateStr);
    return d ? String(d.getFullYear()) === year : false;
  }
  if (period === 'monthly') {
    return matchesAccountsDateFilter(dateStr, year, month);
  }
  if (!year || !quarter) return true;
  const d = parseAccountsDate(dateStr);
  if (!d || String(d.getFullYear()) !== year) return false;
  const q = Math.floor(d.getMonth() / 3) + 1;
  return String(q) === quarter;
}

export function sumBucketUtilizationPkr(
  bucket: AccountsBucket | null | undefined,
  period: AccountsUtilizationPeriod,
  year: string,
  month: string,
  quarter: string,
): number {
  return (bucket?.entries || []).reduce((sum, entry) => {
    if (!matchesAccountsUtilizationPeriod(String(entry.date || ''), period, year, month, quarter)) return sum;
    return sum + (Number(entry.amountPkr) || 0);
  }, 0);
}

export type PassbookAccountType = 'RFA' | 'AAA' | 'DDO';

export function reconcilePassbookByAccount(
  passbookEntries: ReconLike[],
  buckets: Record<PassbookAccountType, ReconLike[]>,
): Record<PassbookAccountType, ReconciliationResult> {
  return {
    RFA: reconcilePassbookWithCashbook(passbookEntries, buckets.RFA),
    AAA: reconcilePassbookWithCashbook(passbookEntries, buckets.AAA),
    DDO: reconcilePassbookWithCashbook(passbookEntries, buckets.DDO),
  };
}

export function getCombinedFundTotals(accounts: AccountsState, year: string) {
  const rfa = getYearAccountData(accounts, year, 'RFA') as AccountsBucket;
  const aaa = getYearAccountData(accounts, year, 'AAA') as AccountsBucket;
  const ddo = getYearAccountData(accounts, year, 'DDO') as AccountsBucket;
  const releasedPkr = (rfa.fundsReleased?.pkr ?? 0) + (aaa.fundsReleased?.pkr ?? 0) + (ddo.fundsReleased?.pkr ?? 0);
  const utilizedPkr = (rfa.fundsUtilization?.pkr ?? 0) + (aaa.fundsUtilization?.pkr ?? 0) + (ddo.fundsUtilization?.pkr ?? 0);
  return {
    rfaPkr: rfa.fundsUtilization?.pkr ?? 0,
    aaaPkr: aaa.fundsUtilization?.pkr ?? 0,
    ddoPkr: ddo.fundsUtilization?.pkr ?? 0,
    rfaUsd: rfa.fundsUtilization?.usd ?? 0,
    aaaUsd: aaa.fundsUtilization?.usd ?? 0,
    ddoUsd: ddo.fundsUtilization?.usd ?? 0,
    releasedPkr,
    utilizedPkr,
    unutilizedPkr: Math.max(0, releasedPkr - utilizedPkr),
  };
}

export function hoaColumnLabel(accountType: string): string {
  return `Head of Account (${accountType})`;
}

export const CASHBOOK_EXCEL_HEADERS = [
  'Date',
  'Cheque No.',
  'Particulars',
  'Amount (PKR)',
  'Balance (PKR)',
  'U$D Rate',
  'Amount (USD)',
  'Balance (USD)',
] as const;

export const HOA_AAA_HEADERS = [
  'Detailed Object Code',
  'Description',
  'Budget Estimates for the FY 2023-24',
  'Budget Estimates for the FY 2024-25 as per PC-1',
  'Revised Budget Estimates of FY 2024-25',
  'Utilization for the FY 2024-25 (PKR)',
  'Balance Funds for the FY 2024-5 (PKR)',
] as const;

export const HOA_RFA_HEADERS = [
  'Sr No',
  'Description',
  'Amount (PKR)',
  'Amount (USD)',
] as const;

export const PARTICULAR_HEADERS = ['Sr No', 'Particulars'] as const;

export interface CashbookParsedRow {
  date: string;
  chequeNo: string;
  particulars: string;
  amountPkr: number;
  balancePkr: number;
  usdRate: number;
  utilizationUsd: number;
  balanceUsd: number;
  headOfAccount: string;
}

export interface AccountFunds {
  pkr: number;
  usd: number;
}

export interface HoasByType {
  RFA?: HoaHead[];
  AAA?: HoaHead[];
  [key: string]: HoaHead[] | undefined;
}

type ReconLike = {
  id?: string;
  amountPkr?: number;
  chequeNo?: string;
  date?: string;
  description?: string;
  particulars?: string;
};

export function fmtAccountsNum(n: unknown): string {
  const v = Number(n);
  if (!v && v !== 0) return '—';
  if (!v) return '—';
  return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

export function fmtAccountsPkr(n: unknown): string {
  const v = Number(n) || 0;
  if (!v) return '—';
  return `PKR ${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export function fmtAccountsUsd(n: unknown): string {
  const v = Number(n) || 0;
  if (!v) return '—';
  return `USD ${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

export function fmtAccountsRate(n: unknown): string {
  const v = Number(n) || 0;
  if (!v) return '—';
  return v.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

export function getYearAccountData(
  accounts: AccountsState | null | undefined,
  year: string,
  type: string,
): AccountsBucket {
  return accounts?.accountsByYear?.[year]?.[type] || {
    entries: [],
    fundsReleased: { pkr: 0, usd: 0 },
    fundsUtilization: { pkr: 0, usd: 0 },
  };
}

function parseNum(v: unknown): number {
  if (v == null || v === '') return 0;
  const n = Number(String(v).replace(/,/g, '').trim());
  return Number.isNaN(n) ? 0 : n;
}

export function parseCashbookRow(
  r: unknown[],
  excelDateFn: (v: number) => string,
): CashbookParsedRow | null {
  const c0 = r[0];
  const chequeNo = String(r[1] ?? '').trim();
  const col2 = String(r[2] ?? '').trim();
  const amountPkr = parseNum(r[3]);
  const balancePkr = parseNum(r[4]);
  const usdRate = parseNum(r[5]);
  const utilizationUsd = parseNum(r[6]);
  const balanceUsd = parseNum(r[7]);
  const headOfAccount = String(r[8] ?? '').trim();

  let date = '';
  let particulars = col2;

  if (typeof c0 === 'number') {
    date = excelDateFn(c0);
  } else {
    const s0 = String(c0 ?? '').trim();
    if (/^\d{1,2}[./-]\d{1,2}[./-]\d{2,4}$/.test(s0)) {
      date = s0.replace(/\//g, '.');
    } else if (s0) {
      particulars = s0;
    }
  }

  if (!date && !chequeNo && !particulars && !amountPkr && !balancePkr && !balanceUsd) {
    return null;
  }

  return {
    date,
    chequeNo,
    particulars: particulars || '—',
    amountPkr,
    balancePkr,
    usdRate,
    utilizationUsd,
    balanceUsd,
    headOfAccount,
  };
}

export function recalcAccountTotals(entries: Array<{ particulars?: string; date?: string; balancePkr?: number; balanceUsd?: number; amountPkr?: number; utilizationUsd?: number }>) {
  let fundsReleased: AccountFunds = { pkr: 0, usd: 0 };
  entries.forEach((e) => {
    const label = `${e.particulars || ''}`.toLowerCase();
    if (label.includes('funds released') || label.includes('opening balance')) {
      if ((e.balancePkr ?? 0) > fundsReleased.pkr) fundsReleased.pkr = e.balancePkr ?? 0;
      if ((e.balanceUsd ?? 0) > fundsReleased.usd) fundsReleased.usd = e.balanceUsd ?? 0;
    }
  });

  const last = entries.length ? entries[entries.length - 1] : null;
  const remainingPkr = Math.max(0, last?.balancePkr ?? fundsReleased.pkr);
  const remainingUsd = Math.max(0, last?.balanceUsd ?? fundsReleased.usd);

  const fundsUtilization: AccountFunds = {
    pkr: Math.max(0, fundsReleased.pkr - remainingPkr),
    usd: Math.max(0, fundsReleased.usd - remainingUsd),
  };
  return { fundsReleased, fundsUtilization };
}

export function parseBankStatementText(text: string): ReconEntry[] {
  const lines = String(text || '').split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  return lines.map((line, i) => {
    const parts = line.split(/[\t,|]/).map((p) => p.trim());
    return {
      id: `bank-${i}`,
      date: parts[0] || '',
      chequeNo: parts[1] || '',
      description: parts[2] || parts[0] || line,
      amountPkr: Number(String(parts[3] || parts[parts.length - 1]).replace(/,/g, '')) || 0,
      raw: line,
    };
  }).filter((r) => r.date || r.chequeNo || r.amountPkr);
}

export function parseBankStatementFile(text: string): ReconEntry[] {
  return parseBankStatementText(text);
}

export function reconcileCashbook(
  cashbookEntries: ReconLike[],
  bankEntries: ReconLike[],
): ReconciliationResult {
  const unmatchedCashbook: ReconEntry[] = [];
  const unmatchedBank = [...bankEntries];
  const matched: ReconciliationMatch[] = [];

  cashbookEntries.forEach((cb) => {
    if (!cb.amountPkr && !cb.chequeNo) return;
    const idx = unmatchedBank.findIndex((b) => {
      const chequeMatch = b.chequeNo && cb.chequeNo && String(b.chequeNo) === String(cb.chequeNo);
      const amtMatch = b.amountPkr && cb.amountPkr && Math.abs(b.amountPkr - cb.amountPkr) < 1;
      if (chequeMatch && amtMatch) return true;
      if (chequeMatch && cb.chequeNo) return amtMatch;
      return false;
    });
    if (idx >= 0) {
      matched.push({ cashbook: cb, bank: unmatchedBank[idx] });
      unmatchedBank.splice(idx, 1);
    } else if ((cb.amountPkr ?? 0) > 0) {
      unmatchedCashbook.push(cb);
    }
  });

  return { matched, unmatchedCashbook, unmatchedBank };
}

export function reconcilePassbookWithCashbook(
  passbookEntries: ReconLike[],
  cashbookEntries: ReconLike[],
): ReconciliationResult {
  const unmatchedPassbook: ReconEntry[] = [];
  const unmatchedCashbook = [...cashbookEntries];
  const matched: ReconciliationMatch[] = [];

  passbookEntries.forEach((pb) => {
    if (!pb.amountPkr && !pb.chequeNo) return;
    const idx = unmatchedCashbook.findIndex((cb) => {
      const chequeMatch = pb.chequeNo && cb.chequeNo && String(pb.chequeNo) === String(cb.chequeNo);
      const amtMatch = pb.amountPkr && cb.amountPkr && Math.abs(pb.amountPkr - cb.amountPkr) < 1;
      return chequeMatch && amtMatch;
    });
    if (idx >= 0) {
      matched.push({ cashbook: pb, bank: unmatchedCashbook[idx] });
      unmatchedCashbook.splice(idx, 1);
    } else if ((pb.amountPkr ?? 0) > 0) {
      unmatchedPassbook.push(pb);
    }
  });

  return { matched, unmatchedCashbook: unmatchedPassbook, unmatchedBank: unmatchedCashbook };
}

export function flattenHoaOptions(
  hoas: Record<string, unknown> | null | undefined,
  accountType: string,
): HoaOption[] {
  const list = (hoas?.[accountType] as HoaHead[] | undefined) || [];
  const options: HoaOption[] = [];
  list.forEach((h) => {
    options.push({ value: h.code, label: `${h.code} — ${h.name}` });
    (h.subHeads || []).forEach((s) => {
      options.push({ value: s.code, label: `${s.code} — ${s.name}` });
    });
  });
  return options;
}

export function flattenParticularOptions(
  particulars: Record<string, unknown> | null | undefined,
  accountType: string,
): ParticularItem[] {
  return (particulars?.[accountType] as ParticularItem[] | undefined) || [];
}

export function collectParticularNamesFromEntries(
  accounts: AccountsState | null | undefined,
  accountType: string,
): string[] {
  const seen = new Set<string>();
  const names: string[] = [];
  Object.values(accounts?.accountsByYear || {}).forEach((yearData) => {
    (yearData?.[accountType]?.entries || []).forEach((entry) => {
      const name = String(entry.particulars || '').trim();
      if (!name || name === '—' || seen.has(name)) return;
      seen.add(name);
      names.push(name);
    });
  });
  return names.sort((a, b) => a.localeCompare(b));
}

export function ensureAccountsExtendedBuckets(accounts: AccountsState): AccountsState {
  const next = JSON.parse(JSON.stringify(accounts)) as AccountsState;
  if (!next.hoas) next.hoas = {};
  if (!next.hoas.DDO?.length) {
    next.hoas.DDO = [
      {
        id: 'DDO-hoa-0',
        code: '01',
        name: 'Salaries & Allowances',
        accountType: 'DDO',
        subHeads: [],
        amountPkr: 0,
        amountUsd: 0,
      },
      {
        id: 'DDO-hoa-1',
        code: '02',
        name: 'Operating Expenses',
        accountType: 'DDO',
        subHeads: [],
        amountPkr: 0,
        amountUsd: 0,
      },
      {
        id: 'DDO-hoa-2',
        code: '03',
        name: 'Transfers from AAA / RFA',
        accountType: 'DDO',
        subHeads: [],
        amountPkr: 0,
        amountUsd: 0,
      },
    ];
  }

  Object.entries(next.accountsByYear || {}).forEach(([year, yearData]) => {
    if (!yearData) return;
    const rfaEntries = (yearData.RFA?.entries || []) as Array<Record<string, unknown>>;
    const aaaEntries = (yearData.AAA?.entries || []) as Array<Record<string, unknown>>;
    const cashbookLines = [...rfaEntries, ...aaaEntries].filter(
      (e) => Number(e.amountPkr) > 0 && String(e.chequeNo || '').trim(),
    );

    if (!yearData.PASSBOOK?.entries?.length && cashbookLines.length) {
      const passbookEntries = cashbookLines.map((e, i) => ({
        ...e,
        id: `PB-${year}-${i + 1}`,
        accountType: 'PASSBOOK',
        financialYear: year,
      }));
      yearData.PASSBOOK = {
        entries: passbookEntries,
        ...recalcAccountTotals(passbookEntries as Parameters<typeof recalcAccountTotals>[0]),
      };
    }

    if (!yearData.DDO?.entries?.length) {
      const ddoTransfers = aaaEntries.filter((e) => /ddo|601000046750089|bop/i.test(String(e.particulars || '')));
      const ddoEntries: Array<Record<string, unknown>> = [];
      if (ddoTransfers.length) {
        let balance = 2500000;
        ddoEntries.push({
          id: `DDO-${year}-ob`,
          accountType: 'DDO',
          financialYear: year,
          date: '',
          chequeNo: '',
          particulars: 'Opening Balance — BOP A/C 601000046750089',
          amountPkr: 0,
          balancePkr: balance,
          usdRate: Number(aaaEntries.find((e) => e.usdRate)?.usdRate) || 218,
          utilizationUsd: 0,
          balanceUsd: 0,
          headOfAccount: '01',
        });
        ddoTransfers.forEach((e, i) => {
          const amt = Number(e.amountPkr) || 0;
          balance += amt;
          ddoEntries.push({
            id: `DDO-${year}-${i + 1}`,
            accountType: 'DDO',
            financialYear: year,
            date: e.date || '',
            chequeNo: e.chequeNo || '',
            particulars: `Transfer received — ${String(e.particulars || 'AAA/RFA')}`,
            amountPkr: amt,
            balancePkr: balance,
            usdRate: e.usdRate ?? 218,
            utilizationUsd: e.utilizationUsd ?? 0,
            balanceUsd: e.balanceUsd ?? 0,
            headOfAccount: '03',
          });
        });
        if (ddoEntries.length > 1) {
          const spend = Math.min(500000, balance * 0.15);
          balance -= spend;
          ddoEntries.push({
            id: `DDO-${year}-util`,
            accountType: 'DDO',
            financialYear: year,
            date: String(ddoTransfers[ddoTransfers.length - 1]?.date || ''),
            chequeNo: String(Number(ddoTransfers[ddoTransfers.length - 1]?.chequeNo || 0) + 1),
            particulars: 'PIU-IWRPP operating expenditure (DDO)',
            amountPkr: spend,
            balancePkr: balance,
            usdRate: Number(ddoTransfers[0]?.usdRate) || 218,
            utilizationUsd: spend / (Number(ddoTransfers[0]?.usdRate) || 218),
            balanceUsd: 0,
            headOfAccount: '02',
          });
        }
      }
      if (ddoEntries.length) {
        yearData.DDO = {
          entries: ddoEntries,
          ...recalcAccountTotals(ddoEntries as Parameters<typeof recalcAccountTotals>[0]),
        };
      } else if (!yearData.DDO) {
        yearData.DDO = {
          entries: [],
          fundsReleased: { pkr: 0, usd: 0 },
          fundsUtilization: { pkr: 0, usd: 0 },
        };
      }
    }

    ['RFA', 'AAA', 'PASSBOOK', 'DDO'].forEach((type) => {
      const bucket = yearData[type as keyof typeof yearData] as AccountsBucket | undefined;
      if (bucket?.entries?.length) {
        Object.assign(bucket, recalcAccountTotals(bucket.entries));
      }
    });

    ['PASSBOOK', 'DDO'].forEach((type) => {
      if (!yearData[type]) {
        yearData[type] = {
          entries: [],
          fundsReleased: { pkr: 0, usd: 0 },
          fundsUtilization: { pkr: 0, usd: 0 },
        };
      }
    });
  });

  return next;
}

export function ensureAccountsParticulars(accounts: AccountsState): AccountsState {
  const next = ensureAccountsExtendedBuckets(accounts);
  if (!next.particulars) next.particulars = { RFA: [], AAA: [] };
  ACCOUNT_TYPES.forEach((type) => {
    const list = next.particulars?.[type];
    if (Array.isArray(list) && list.length) return;
    const names = collectParticularNamesFromEntries(next, type);
    next.particulars![type] = names.map((name, index) => ({
      id: `part-${type}-${index + 1}`,
      name,
      accountType: type,
    }));
  });
  return next;
}
